import * as Location from 'expo-location';
import { AppState } from 'react-native';
import { isWeb } from '@/utils/navigation';
import { useEffect, useState } from 'react';

type PermissionState = 'denied' | 'granted' | 'prompt' | 'unknown';

const toPermissionState = (status: Location.PermissionStatus) =>
  status === Location.PermissionStatus.GRANTED
    ? 'granted'
    : status === Location.PermissionStatus.DENIED
      ? 'denied'
      : 'prompt';

/**
 * Tracks whether the rep still owes an answer to the OS location prompt, so a
 * caller can explain the request before the OS asks. Re-reads the status on
 * every foreground return, so a grant changed in the device settings counts.
 */
export const useLocationPermissionPriming = () => {
  const [permissionState, setPermissionState] =
    useState<PermissionState>('unknown');
  useEffect(() => {
    if (isWeb) {
      return;
    }
    let cancelled = false;
    const readPermissionState = () => {
      Location.getForegroundPermissionsAsync()
        .then(({ status }) => {
          if (!cancelled) {
            setPermissionState(toPermissionState(status));
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPermissionState('prompt');
          }
        });
    };
    readPermissionState();
    const subscription = AppState.addEventListener('change', (status) => {
      if (status === 'active') {
        readPermissionState();
      }
    });
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, []);

  const acceptPriming = async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status === Location.PermissionStatus.GRANTED) {
        setPermissionState('granted');
      } else if (status === Location.PermissionStatus.DENIED) {
        setPermissionState('denied');
      }
    } catch {
      setPermissionState('denied');
    }
  };

  return {
    acceptPriming,
    shouldPrime: permissionState === 'prompt',
  };
};
